import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ChevronLeft, Gamepad2, Info, Pencil, Store, Users, Wallet, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  Button,
  Checkbox,
  DataTableShell,
  Input,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@kioskgaming/ui';
import { PageLoadingIndicator } from '@kioskgaming/page-loading';
import AppLayout from '../components/layout/AppLayout';
import SellCreditsCashToAgentTrigger from '../components/credits/SellCreditsCashToAgentTrigger';
import WithdrawCreditsFromAgentTrigger from '../components/credits/WithdrawCreditsFromAgentTrigger';
import { useAuth } from '../hooks/useAuth';
import {
  fetchManagedAgentGameMappings,
  fetchManagedAgents,
  fetchManagedPlayers,
  fetchManagedShops,
  fetchPublicGameProviders,
  putManagedAgentGameMappings,
  suspendManagedAgent,
  unsuspendManagedAgent,
  updateManagedAgentPassword,
  updateManagedAgentRate,
  updateManagedAgentName,
  updateManagedAgentPopupPermissions,
} from '../services/api';
import type { SuperManagedAgent, SuperManagedPlayer, SuperManagedShop } from '../types';

const toPercent = (rate: number | string | undefined) => {
  const n = Number(rate ?? 0);
  if (!Number.isFinite(n)) return '0.00';
  return (n * 100).toFixed(2);
};

const percentStrToCostRate = (percentStr: string) => {
  const n = Number(percentStr);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.min(1, Number((n / 100).toFixed(4)));
};

const isValidCostPercentInput = (value: string) => {
  if (value.trim() === '') return false;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 && n <= 100;
};

const statusBadgeClassName: Record<SuperManagedAgent['status'], string> = {
  active: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  suspended: 'border-amber-200 bg-amber-50 text-amber-800',
  terminated: 'border-red-200 bg-red-50 text-red-700',
};

const AGENT_TABS = ['info', 'games', 'shops', 'players'] as const;
type AgentDetailTab = (typeof AGENT_TABS)[number];
const MAX_AGENT_NAME_LENGTH = 100;

function agentCanCreateShopPopup(agent: SuperManagedAgent): boolean {
  return !!(agent.canCreateShopPopup ?? agent.can_create_shop_popup);
}

function agentCanManageShopPopupPermission(agent: SuperManagedAgent): boolean {
  return !!(agent.canManageShopPopupPermission ?? agent.can_manage_shop_popup_permission);
}

function agentDefaultShopCanCreatePlayerPopup(agent: SuperManagedAgent): boolean {
  return !!(agent.defaultShopCanCreatePlayerPopup ?? agent.default_shop_can_create_player_popup);
}

function isAgentDetailTab(v: string | null): v is AgentDetailTab {
  return v === 'info' || v === 'games' || v === 'shops' || v === 'players';
}

function formatAgentCreditBalance(n: number | undefined) {
  if (n == null || !Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 });
}

export default function ManagedAgentDetailPage() {
  const { agentId } = useParams<{ agentId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: AgentDetailTab = isAgentDetailTab(tabParam) ? tabParam : 'info';
  const { user: superAgentProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editingCostRate, setEditingCostRate] = useState(false);
  const [costPercentDraft, setCostPercentDraft] = useState('');
  const [savingRate, setSavingRate] = useState(false);
  const [passwordDraft, setPasswordDraft] = useState('');
  const [passwordConfirmDraft, setPasswordConfirmDraft] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [savingPopupField, setSavingPopupField] = useState<
    'can_create_shop_popup' | 'can_manage_shop_popup_permission' | 'default_shop_can_create_player_popup' | null
  >(null);
  const [agent, setAgent] = useState<SuperManagedAgent | null>(null);
  const [shops, setShops] = useState<SuperManagedShop[]>([]);
  const [players, setPlayers] = useState<SuperManagedPlayer[]>([]);
  const [shopNameSort, setShopNameSort] = useState<'asc' | 'desc'>('asc');
  const [playerNameSort, setPlayerNameSort] = useState<'asc' | 'desc'>('asc');
  const [gamesLoading, setGamesLoading] = useState(false);
  const [gameMode, setGameMode] = useState<'all' | 'selected'>('all');
  const [selectedGameIds, setSelectedGameIds] = useState<string[]>([]);
  const [parentGamePool, setParentGamePool] = useState<string[]>([]);
  const [gameProviders, setGameProviders] = useState<Array<{ id: string; name: string; code: string }>>([]);
  const [savingGames, setSavingGames] = useState(false);

  const load = useCallback(async () => {
    if (!agentId) return;
    setLoading(true);
    try {
      const [agentsRes, shopsRes, playersRes] = await Promise.all([
        fetchManagedAgents(),
        fetchManagedShops(),
        fetchManagedPlayers(),
      ]);
      const foundAgent = (agentsRes.data ?? []).find((a) => a.id === agentId) ?? null;
      setAgent(foundAgent);
      setShops((shopsRes.data ?? []).filter((s) => s.agent?.id === agentId));
      setPlayers((playersRes.data ?? []).filter((p) => p.agent_id === agentId));
    } catch {
      toast.error('Could not load agent detail');
      setAgent(null);
      setShops([]);
      setPlayers([]);
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (activeTab !== 'games' || !agentId) return;
    let cancelled = false;
    (async () => {
      setGamesLoading(true);
      try {
        const [mapRes, gpRes] = await Promise.all([
          fetchManagedAgentGameMappings(agentId),
          fetchPublicGameProviders(),
        ]);
        if (cancelled) return;
        const ids = mapRes.data?.game_ids ?? [];
        const parent = mapRes.data?.super_agent_game_ids ?? [];
        const items =
          gpRes.data?.items?.filter((p) => p.status !== 'maintenance').map((p) => ({ id: p.id, name: p.name, code: p.code })) ??
          [];
        setParentGamePool(parent);
        setGameProviders(items);
        if (ids.length === 0) {
          setGameMode('all');
          setSelectedGameIds([]);
        } else {
          setGameMode('selected');
          setSelectedGameIds(ids);
        }
      } catch {
        if (!cancelled) toast.error('Could not load game settings');
      } finally {
        if (!cancelled) setGamesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab, agentId]);

  const visibleGameProviders = useMemo(() => {
    if (parentGamePool.length === 0) return gameProviders;
    const set = new Set(parentGamePool);
    return gameProviders.filter((p) => set.has(p.id));
  }, [gameProviders, parentGamePool]);

  const toggleGameSelection = (gid: string) => {
    setSelectedGameIds((prev) => (prev.includes(gid) ? prev.filter((x) => x !== gid) : [...prev, gid]));
  };

  const saveGames = async () => {
    if (!agentId || savingGames) return;
    const payload = gameMode === 'all' ? [] : selectedGameIds;
    setSavingGames(true);
    try {
      const res = await putManagedAgentGameMappings(agentId, payload);
      if (!res.success) {
        toast.error((res as { message?: string }).message || 'Could not save games');
        return;
      }
      toast.success('Game list saved');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Could not save games';
      toast.error(msg);
    } finally {
      setSavingGames(false);
    }
  };

  const activeShopCount = useMemo(
    () => shops.filter((shop) => shop.status === 'active').length,
    [shops],
  );
  const activePlayerCount = useMemo(
    () => players.filter((player) => player.status === 'active').length,
    [players],
  );

  const sortedShops = useMemo(() => {
    const dir = shopNameSort === 'asc' ? 1 : -1;
    return [...shops].sort((a, b) => a.name.localeCompare(b.name) * dir);
  }, [shops, shopNameSort]);

  const sortedPlayers = useMemo(() => {
    const dir = playerNameSort === 'asc' ? 1 : -1;
    return [...players].sort((a, b) => {
      const an = String(a.username || a.email || a.id).toLowerCase();
      const bn = String(b.username || b.email || b.id).toLowerCase();
      return an.localeCompare(bn) * dir;
    });
  }, [players, playerNameSort]);

  const onSuspendToggle = async () => {
    if (!agent) return;
    setBusy(true);
    try {
      const res = agent.status === 'suspended'
        ? await unsuspendManagedAgent(agent.id)
        : await suspendManagedAgent(agent.id);
      if (!res.success) {
        toast.error(res.message || 'Action failed');
        return;
      }
      toast.success(agent.status === 'suspended' ? 'Agent unsuspended' : 'Agent suspended');
      await load();
    } catch {
      toast.error('Action failed');
    } finally {
      setBusy(false);
    }
  };

  const parentCostPercent = useMemo(() => {
    const r = Number(superAgentProfile?.costRate ?? 0);
    if (!Number.isFinite(r)) return 0;
    return r * 100;
  }, [superAgentProfile?.costRate]);

  const startEditCostRate = () => {
    if (!agent) return;
    setCostPercentDraft(toPercent(agent.costRate));
    setEditingCostRate(true);
  };

  const cancelEditCostRate = () => {
    setEditingCostRate(false);
    setCostPercentDraft('');
  };

  const saveCostRate = async () => {
    if (!agent || agent.status === 'terminated') return;
    if (!isValidCostPercentInput(costPercentDraft)) {
      toast.error('Enter a cost rate between 0 and 100 (percent)');
      return;
    }
    const rate = percentStrToCostRate(costPercentDraft);
    if (rate == null) {
      toast.error('Invalid cost rate');
      return;
    }
    setSavingRate(true);
    try {
      const res = await updateManagedAgentRate(agent.id, rate);
      if (!res.success || !res.data) {
        toast.error(res.message || 'Could not update cost rate');
        return;
      }
      toast.success('Cost rate updated');
      setAgent(res.data);
      setEditingCostRate(false);
      setCostPercentDraft('');
      await load();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Could not update cost rate';
      toast.error(msg);
    } finally {
      setSavingRate(false);
    }
  };

  const startEditName = () => {
    if (!agent) return;
    setNameDraft(agent.name);
    setEditingName(true);
  };

  const cancelEditName = () => {
    setEditingName(false);
    setNameDraft('');
  };

  const saveName = async () => {
    if (!agent || agent.status === 'terminated' || savingName) return;
    const trimmed = nameDraft.trim();
    if (!trimmed) {
      toast.error('Display name is required');
      return;
    }
    if (trimmed.length > MAX_AGENT_NAME_LENGTH) {
      toast.error(`Display name must be at most ${MAX_AGENT_NAME_LENGTH} characters`);
      return;
    }
    setSavingName(true);
    try {
      const res = await updateManagedAgentName(agent.id, trimmed);
      if (!res.success || !res.data) {
        toast.error(res.message || 'Could not update display name');
        return;
      }
      toast.success('Display name updated');
      setAgent(res.data);
      setEditingName(false);
      setNameDraft('');
      await load();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Could not update display name';
      toast.error(msg);
    } finally {
      setSavingName(false);
    }
  };

  const patchPopupPermission = async (
    field: 'can_create_shop_popup' | 'can_manage_shop_popup_permission' | 'default_shop_can_create_player_popup',
    nextValue: boolean,
  ) => {
    if (!agent || agent.status === 'terminated' || savingPopupField) return;
    setSavingPopupField(field);
    try {
      const res = await updateManagedAgentPopupPermissions(agent.id, { [field]: nextValue });
      if (!res.success || !res.data) {
        toast.error(res.message || 'Could not update popup permissions');
        return;
      }
      toast.success('Popup permissions updated');
      setAgent(res.data);
      await load();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Could not update popup permissions';
      toast.error(msg);
    } finally {
      setSavingPopupField(null);
    }
  };

  const savePassword = async () => {
    if (!agent || agent.status === 'terminated' || savingPassword) return;
    const nextPassword = passwordDraft.trim();
    const confirmPassword = passwordConfirmDraft.trim();
    if (nextPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (nextPassword !== confirmPassword) {
      toast.error('Password confirmation does not match');
      return;
    }
    setSavingPassword(true);
    try {
      const res = await updateManagedAgentPassword(agent.id, nextPassword);
      if (!res.success) {
        toast.error(res.message || 'Could not update password');
        return;
      }
      toast.success(res.message || 'Password updated');
      setPasswordDraft('');
      setPasswordConfirmDraft('');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Could not update password';
      toast.error(msg);
    } finally {
      setSavingPassword(false);
    }
  };

  const setActiveTab = (t: AgentDetailTab) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (t === 'info') {
          next.delete('tab');
        } else {
          next.set('tab', t);
        }
        return next;
      },
      { replace: true },
    );
  };

  return (
    <AppLayout contentFullWidth>
      <div className="mb-4">
        <Link
          to="/agents"
          className="inline-flex items-center gap-0.5 text-sm font-semibold text-orange-700 hover:text-orange-900"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden />
          Back to agents
        </Link>
      </div>

      {loading ? (
        <PageLoadingIndicator variant="page" />
      ) : !agent ? (
        <div className="rounded-xl border border-dashed border-gray-200 bg-white p-12 text-center">
          <p className="text-gray-600">Agent not found</p>
          <Link to="/agents" className="mt-3 inline-block text-sm font-semibold text-orange-700 hover:text-orange-900">
            Return to agent list
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          <div
            className="flex gap-1 rounded-xl border border-gray-200 bg-gray-50/80 p-1 sm:inline-flex sm:flex-wrap"
            role="tablist"
            aria-label="Agent sections"
          >
            <Button
              type="button"
              role="tab"
              aria-selected={activeTab === 'info'}
              variant={activeTab === 'info' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab('info')}
              className={`flex flex-1 gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold sm:flex-initial sm:px-4 ${
                activeTab === 'info' ? 'shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Info className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
              Info
            </Button>
            <Button
              type="button"
              role="tab"
              aria-selected={activeTab === 'games'}
              variant={activeTab === 'games' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab('games')}
              className={`flex flex-1 gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold sm:flex-initial sm:px-4 ${
                activeTab === 'games' ? 'shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Gamepad2 className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
              Games
            </Button>
            <Button
              type="button"
              role="tab"
              aria-selected={activeTab === 'shops'}
              variant={activeTab === 'shops' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab('shops')}
              className={`flex flex-1 gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold sm:flex-initial sm:px-4 ${
                activeTab === 'shops' ? 'shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Store className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
              Shops
              <span className="rounded-full bg-gray-200/80 px-1.5 py-0.5 text-xs font-bold text-gray-700 tabular-nums">
                {shops.length}
              </span>
            </Button>
            <Button
              type="button"
              role="tab"
              aria-selected={activeTab === 'players'}
              variant={activeTab === 'players' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab('players')}
              className={`flex flex-1 gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold sm:flex-initial sm:px-4 ${
                activeTab === 'players' ? 'shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Users className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
              Players
              <span className="rounded-full bg-gray-200/80 px-1.5 py-0.5 text-xs font-bold text-gray-700 tabular-nums">
                {players.length}
              </span>
            </Button>
          </div>

          {activeTab === 'info' && (
            <div className="space-y-6">
              <section className="overflow-hidden rounded-xl border border-orange-100 bg-white shadow-sm">
                <div className="border-b border-gray-100 bg-gradient-to-br from-white to-orange-50/30 px-5 py-5 sm:px-6">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0">
                      <h1 className="max-w-full truncate text-2xl font-bold tracking-tight text-gray-900" title={agent.name}>
                        {agent.name}
                      </h1>
                      <p className="mt-1 text-sm text-gray-500">{agent.email}</p>
                      <p className="mt-0.5 text-sm text-gray-500">
                        Username:{' '}
                        <span className="inline-block max-w-full truncate align-bottom font-medium text-gray-700" title={agent.username || undefined}>
                          {agent.username?.trim() ? agent.username : '—'}
                        </span>
                      </p>
                    </div>
                    {agent.status !== 'terminated' && (
                      <div className="flex flex-shrink-0 flex-wrap items-center gap-2 lg:pt-0.5">
                        <SellCreditsCashToAgentTrigger
                          agentId={agent.id}
                          agentName={agent.name}
                          agentCostRate={agent.costRate}
                          onSuccess={() => void load()}
                          size="sm"
                          className="shadow-sm"
                        />
                        <WithdrawCreditsFromAgentTrigger
                          agentId={agent.id}
                          agentName={agent.name}
                          agentCostRate={agent.costRate}
                          sourceCreditBalance={agent.credit_balance}
                          onSuccess={() => void load()}
                          size="sm"
                          className="shadow-sm"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="border-amber-200 bg-amber-50 text-amber-900 shadow-sm hover:bg-amber-100"
                          onClick={() => void onSuspendToggle()}
                          disabled={busy}
                        >
                          {agent.status === 'suspended' ? 'Unsuspend' : 'Suspend'}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid gap-px bg-gray-100 sm:grid-cols-2">
                  <div className="flex items-center gap-3 bg-white px-5 py-4 sm:px-6">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Status</p>
                      <span
                        className={`mt-1 inline-flex rounded-full border px-3 py-1 text-xs font-semibold uppercase ${statusBadgeClassName[agent.status]}`}
                      >
                        {agent.status}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-white px-5 py-4 sm:px-6">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-orange-100 text-orange-700">
                      <Wallet className="h-5 w-5" aria-hidden />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Credit balance</p>
                      <p className="mt-0.5 text-xl font-semibold tabular-nums text-gray-900">
                        {formatAgentCreditBalance(agent.credit_balance)}
                        <span className="ml-1.5 text-sm font-normal text-gray-500">credits</span>
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              <section className="rounded-xl border border-orange-100 bg-white p-5 shadow-sm">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm text-gray-500">Display name</p>
                  {agent.status !== 'terminated' && !editingName && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={startEditName}
                      className="h-8 gap-1 px-2 text-xs font-semibold"
                      disabled={busy || savingName}
                    >
                      <Pencil className="h-3.5 w-3.5" aria-hidden />
                      Edit
                    </Button>
                  )}
                </div>
                {editingName ? (
                  <div className="mt-3 flex flex-wrap items-end gap-2">
                    <Input
                      type="text"
                      value={nameDraft}
                      onChange={(e) => setNameDraft(e.target.value)}
                      className="min-w-[200px] max-w-md flex-1 text-base font-semibold"
                      aria-label="Display name"
                      maxLength={MAX_AGENT_NAME_LENGTH}
                      disabled={savingName}
                    />
                    <Button
                      type="button"
                      onClick={() => void saveName()}
                      disabled={savingName || nameDraft.trim() === ''}
                      loading={savingName}
                      size="sm"
                    >
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={cancelEditName}
                      disabled={savingName}
                      className="gap-1"
                    >
                      <X className="h-4 w-4" aria-hidden />
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <p className="mt-2 max-w-full truncate text-xl font-semibold text-gray-900" title={agent.name}>
                    {agent.name}
                  </p>
                )}
              </section>

              <section className="rounded-xl border border-orange-100 bg-white p-5 shadow-sm">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm text-gray-500">Cost rate</p>
                  {agent.status !== 'terminated' && !editingCostRate && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={startEditCostRate}
                      className="h-8 gap-1 px-2 text-xs font-semibold"
                      disabled={busy || savingRate}
                    >
                      <Pencil className="h-3.5 w-3.5" aria-hidden />
                      Edit
                    </Button>
                  )}
                </div>
                {editingCostRate ? (
                  <div className="mt-3 space-y-2">
                    <div className="flex flex-wrap items-end gap-2">
                      <div className="flex items-end gap-1">
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          step={0.01}
                          value={costPercentDraft}
                          onChange={(e) => setCostPercentDraft(e.target.value)}
                          className="w-28 text-lg font-semibold"
                          inputSize="lg"
                          aria-label="Cost rate percent"
                        />
                        <span className="pb-2 text-lg font-semibold text-gray-700">%</span>
                      </div>
                      <Button
                        type="button"
                        onClick={() => void saveCostRate()}
                        disabled={savingRate || !isValidCostPercentInput(costPercentDraft)}
                        loading={savingRate}
                        size="sm"
                      >
                        Save
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={cancelEditCostRate}
                        disabled={savingRate}
                        className="gap-1"
                      >
                        <X className="h-4 w-4" aria-hidden />
                        Cancel
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500">
                      Must be at least your super agent rate ({parentCostPercent.toFixed(2)}%).
                    </p>
                  </div>
                ) : (
                  <p className="mt-2 text-2xl font-semibold text-gray-900">{toPercent(agent.costRate)}%</p>
                )}
              </section>

              {superAgentProfile?.can_delegate_popups && agent.status !== 'terminated' && (
                <section className="rounded-xl border border-orange-100 bg-white p-5 shadow-sm">
                  <p className="text-sm font-medium text-gray-900">In-app popups / broadcasts (delegated)</p>
                  <p className="mt-1 text-xs text-gray-500">
                    Change what this agent can do for shops and players. Updates apply immediately.
                  </p>
                  <label className="mt-4 flex cursor-pointer items-start gap-3">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={agentCanCreateShopPopup(agent)}
                      disabled={savingPopupField !== null}
                      onChange={(e) => void patchPopupPermission('can_create_shop_popup', e.target.checked)}
                    />
                    <span className="text-sm text-gray-800">
                      <span className="font-medium">Allow this agent to send popups/broadcasts to shops/players</span>
                      <span className="mt-0.5 block text-xs text-gray-500">
                        When enabled, the agent can send delegated popups/broadcasts.
                      </span>
                    </span>
                  </label>
                  <label className="mt-3 flex cursor-pointer items-start gap-3">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={agentCanManageShopPopupPermission(agent)}
                      disabled={savingPopupField !== null}
                      onChange={(e) =>
                        void patchPopupPermission('can_manage_shop_popup_permission', e.target.checked)
                      }
                    />
                    <span className="text-sm text-gray-800">
                      <span className="font-medium">Allow this agent to manage popup permission for its shops</span>
                      <span className="mt-0.5 block text-xs text-gray-500">
                        Agent can enable/disable shop popup permission per shop.
                      </span>
                    </span>
                  </label>
                  <label className="mt-3 flex cursor-pointer items-start gap-3">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={agentDefaultShopCanCreatePlayerPopup(agent)}
                      disabled={savingPopupField !== null}
                      onChange={(e) =>
                        void patchPopupPermission('default_shop_can_create_player_popup', e.target.checked)
                      }
                    />
                    <span className="text-sm text-gray-800">
                      <span className="font-medium">Default: new shops can send popups/broadcasts to players</span>
                      <span className="mt-0.5 block text-xs text-gray-500">
                        When the agent creates a shop, that shop can inherit this default.
                      </span>
                    </span>
                  </label>
                  {savingPopupField ? (
                    <p className="mt-2 text-xs text-gray-500">Saving…</p>
                  ) : null}
                </section>
              )}

              <section className="rounded-xl border border-orange-100 bg-white p-5 shadow-sm">
                <p className="text-sm text-gray-500">Reset password</p>
                <div className="mt-3 grid gap-3 sm:max-w-lg">
                  <Input
                    type="password"
                    value={passwordDraft}
                    onChange={(e) => setPasswordDraft(e.target.value)}
                    placeholder="New password (min 8 chars)"
                    autoComplete="new-password"
                    disabled={agent.status === 'terminated' || savingPassword}
                  />
                  <Input
                    type="password"
                    value={passwordConfirmDraft}
                    onChange={(e) => setPasswordConfirmDraft(e.target.value)}
                    placeholder="Confirm new password"
                    autoComplete="new-password"
                    disabled={agent.status === 'terminated' || savingPassword}
                  />
                  <div>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => void savePassword()}
                      disabled={
                        savingPassword ||
                        agent.status === 'terminated' ||
                        passwordDraft.trim().length < 8 ||
                        passwordConfirmDraft.trim().length < 8
                      }
                      loading={savingPassword}
                    >
                      Update password
                    </Button>
                  </div>
                </div>
              </section>
            </div>
          )}

          {activeTab === 'games' && (
            <div className="space-y-4 rounded-xl border border-orange-100 bg-white p-5 shadow-sm">
              <h2 className="text-sm font-semibold text-gray-900">Games for this agent</h2>
              <p className="text-xs text-gray-500">
                {parentGamePool.length === 0
                  ? 'Super agent pool: all games. You may restrict to a subset below.'
                  : `Super agent allows ${parentGamePool.length} game(s). Only those appear in the list.`}
              </p>
              {gamesLoading ? (
                <PageLoadingIndicator variant="inline" />
              ) : (
                <>
                  <RadioGroup
                    name="managed-agent-game-mode"
                    value={gameMode}
                    onChange={(v) => setGameMode(v as 'all' | 'selected')}
                    disabled={agent?.status === 'terminated'}
                    options={[
                      { value: 'all', label: 'All games in super-agent pool' },
                      { value: 'selected', label: 'Selected only' },
                    ]}
                    className="max-w-xl"
                  />
                  {gameMode === 'selected' ? (
                    <div className="max-h-64 overflow-y-auto rounded-lg border border-gray-100 p-3 space-y-2">
                      {visibleGameProviders.length === 0 ? (
                        <p className="text-sm text-gray-500">No games available.</p>
                      ) : (
                        visibleGameProviders.map((p) => (
                          <Checkbox
                            key={p.id}
                            checked={selectedGameIds.includes(p.id)}
                            onChange={() => toggleGameSelection(p.id)}
                            disabled={agent?.status === 'terminated'}
                            label={
                              <span className="inline-flex flex-wrap items-baseline gap-x-1.5 gap-y-0">
                                <span>{p.name}</span>
                                <span className="text-xs font-normal text-gray-400">({p.code})</span>
                              </span>
                            }
                            className="py-0.5"
                          />
                        ))
                      )}
                    </div>
                  ) : null}
                  <Button
                    type="button"
                    onClick={() => void saveGames()}
                    disabled={savingGames || agent?.status === 'terminated'}
                    loading={savingGames}
                  >
                    Save
                  </Button>
                </>
              )}
            </div>
          )}

          {activeTab === 'shops' && (
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-xl border border-orange-100 bg-white p-5 shadow-sm">
                  <p className="flex items-center gap-2 text-sm text-gray-500">
                    <Store className="h-4 w-4" aria-hidden />
                    Total shops
                  </p>
                  <p className="mt-2 text-2xl font-semibold text-gray-900">{shops.length}</p>
                </div>
                <div className="rounded-xl border border-orange-100 bg-white p-5 shadow-sm">
                  <p className="text-sm text-gray-500">Active shops</p>
                  <p className="mt-2 text-2xl font-semibold text-gray-900">{activeShopCount}</p>
                </div>
              </div>
              <div className="overflow-hidden rounded-xl border border-orange-100 bg-white shadow-sm">
                <div className="border-b border-gray-100 px-4 py-3">
                  <h2 className="text-sm font-semibold text-gray-900">Managed shops</h2>
                </div>
                <DataTableShell borderless className="rounded-none bg-transparent shadow-none">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-gray-100 hover:bg-transparent">
                        <TableHead>
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 hover:text-gray-900"
                            onClick={() => setShopNameSort((d) => (d === 'asc' ? 'desc' : 'asc'))}
                          >
                            Shop{' '}
                            <span className="text-[10px] text-gray-400" aria-hidden>
                              {shopNameSort === 'asc' ? '↑' : '↓'}
                            </span>
                          </button>
                        </TableHead>
                        <TableHead>Username</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedShops.length === 0 ? (
                        <TableRow className="hover:bg-transparent">
                          <TableCell className="py-8 text-center text-muted-foreground" colSpan={4}>
                            No shops under this agent yet.
                          </TableCell>
                        </TableRow>
                      ) : (
                        sortedShops.map((shop) => (
                          <TableRow key={shop.id}>
                            <TableCell className="font-medium text-foreground">
                              <div className="max-w-[260px]">
                                <Link
                                  to={`/shops/${shop.id}`}
                                  className="block truncate text-blue-700 underline-offset-2 hover:text-blue-800 hover:underline"
                                  title={shop.name}
                                >
                                  {shop.name}
                                </Link>
                              </div>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              <span className="block max-w-[220px] truncate" title={shop.username || undefined}>
                                {shop.username?.trim() ? shop.username : '—'}
                              </span>
                            </TableCell>
                            <TableCell>{shop.email}</TableCell>
                            <TableCell className="uppercase">{shop.status}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </DataTableShell>
              </div>
            </div>
          )}

          {activeTab === 'players' && (
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-xl border border-orange-100 bg-white p-5 shadow-sm">
                  <p className="flex items-center gap-2 text-sm text-gray-500">
                    <Users className="h-4 w-4" aria-hidden />
                    Total players
                  </p>
                  <p className="mt-2 text-2xl font-semibold text-gray-900">{players.length}</p>
                </div>
                <div className="rounded-xl border border-orange-100 bg-white p-5 shadow-sm">
                  <p className="text-sm text-gray-500">Active players</p>
                  <p className="mt-2 text-2xl font-semibold text-gray-900">{activePlayerCount}</p>
                </div>
              </div>
              <div className="overflow-hidden rounded-xl border border-orange-100 bg-white shadow-sm">
                <div className="border-b border-gray-100 px-4 py-3">
                  <h2 className="text-sm font-semibold text-gray-900">Managed players</h2>
                </div>
                <DataTableShell borderless className="rounded-none bg-transparent shadow-none">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-gray-100 hover:bg-transparent">
                        <TableHead>
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 hover:text-gray-900"
                            onClick={() => setPlayerNameSort((d) => (d === 'asc' ? 'desc' : 'asc'))}
                          >
                            Player{' '}
                            <span className="text-[10px] text-gray-400" aria-hidden>
                              {playerNameSort === 'asc' ? '↑' : '↓'}
                            </span>
                          </button>
                        </TableHead>
                        <TableHead>Shop</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedPlayers.length === 0 ? (
                        <TableRow className="hover:bg-transparent">
                          <TableCell className="py-8 text-center text-muted-foreground" colSpan={3}>
                            No players under this agent yet.
                          </TableCell>
                        </TableRow>
                      ) : (
                        sortedPlayers.map((player) => (
                          <TableRow key={player.id}>
                            <TableCell className="font-medium text-foreground">
                              <span
                                className="block max-w-[280px] truncate"
                                title={player.username || player.email || player.phone || player.id}
                              >
                                {player.username || player.email || player.phone || player.id}
                              </span>
                            </TableCell>
                            <TableCell>{player.shop_name || '-'}</TableCell>
                            <TableCell className="uppercase">{player.status}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </DataTableShell>
              </div>
            </div>
          )}
        </div>
      )}

    </AppLayout>
  );
}
